import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ControlledModal from '../Modal/ControlledModal';
import { Button } from '../ui/button';
import { useResendTalentInviteMutation } from '../../reactWrapper/Redux/apiSlice';

/**
 * Send a talent their invite again, with a fresh pitch.
 *
 * A resend is rarely the same argument as the first attempt. The original
 * went unanswered, so sending those exact words again is the one thing we
 * already know did not work - hence the subject and message are editable
 * here rather than simply repeated.
 *
 * The link is not one of the fields. The template renders the button and
 * puts a newly minted token behind it, so a resend cannot go out with a
 * dead link or no link at all. What the admin controls is what the button
 * says.
 */

const DEFAULT_SUBJECT = 'Action Required: Claim your Droomwork profile to access new jobs';

const DEFAULT_MESSAGE = `Hi {{firstName}},

You joined the Droomwork network early, and we have exciting news. Our fully upgraded talent platform is now live, and global employers are actively hiring.

Because you are already in our database, you have priority access. To start matching you with these high-paying remote roles - spanning tech, creative and professional industries - you need to complete your profile.

It takes less than 2 minutes to migrate your details and get visible to hiring managers.

Don't leave your next big opportunity waiting.

Best,
The Droomwork Team`;

const DEFAULT_CTA = 'Claim Your Profile & Get Matched';

function describeError(err) {
  if (err && err.data && err.data.error && err.data.error.message) {
    return err.data.error.message;
  }
  if (err && err.data && err.data.message) {
    return err.data.message;
  }
  if (err && err.error) {
    return err.error;
  }
  return 'Could not send the invite';
}

const ResendInviteModal = ({ open, talent, onClose }) => {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_CTA);
  const [error, setError] = useState(null);

  const [resend, { isLoading }] = useResendTalentInviteMutation();

  useEffect(() => {
    if (!open) {
      return;
    }
    setSubject(DEFAULT_SUBJECT);
    setMessage(DEFAULT_MESSAGE);
    setCtaLabel(DEFAULT_CTA);
    setError(null);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const out = await resend({
        userId: talent.id,
        subject: subject.trim(),
        message: message.trim(),
        ctaLabel: ctaLabel.trim(),
      }).unwrap();

      toast.success(out?.message || 'Invite sent');
      onClose?.();
    } catch (err) {
      setError(describeError(err));
    }
  };

  let canSend = true;

  if (isLoading) {
    canSend = false;
  } else if (subject.trim() === '' || message.trim() === '') {
    canSend = false;
  }

  const email = talent ? talent.email : '';

  return (
    <ControlledModal
      open={open}
      closeModal={() => !isLoading && onClose?.()}
      closeOnDocumentClick={!isLoading}
      contentStyle={{
        width: 'min(640px, 97vw)',
        borderRadius: '12px',
        padding: 0,
        border: 'none',
        background: 'transparent',
      }}
    >
      <form onSubmit={submit} className='flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-card'>
        <div className='flex items-center justify-between border-b border-border px-6 py-4'>
          <h2 className='text-lg font-bold text-brand-navy'>Resend invite</h2>
          <button
            type='button'
            onClick={() => onClose?.()}
            disabled={isLoading}
            className='text-sm text-muted-foreground hover:text-foreground'
          >
            Close
          </button>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5'>
          <p className='text-sm text-muted-foreground'>
            Sending to <span className='font-medium text-foreground'>{email}</span>. A new
            set-password link is made each time, so any earlier link stops working.
          </p>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>Subject</span>
            <input
              type='text'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isLoading}
              maxLength={150}
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>Message</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading}
              rows={12}
              className='rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
            <span className='text-xs text-muted-foreground'>
              {'Plain text. Leave a blank line between paragraphs. Use {{firstName}}, '}
              {'{{lastName}} or {{fullName}} — single braces will not be replaced. Do not '}
              paste a link; the button below the message is added for you.
            </span>
          </label>

          <label className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-foreground'>Button wording</span>
            <input
              type='text'
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              disabled={isLoading}
              maxLength={80}
              className='h-10 rounded-md border border-input bg-background px-3 text-sm'
            />
            <span className='text-xs text-muted-foreground'>
              What the orange button says. It always links to their set-password page.
            </span>
          </label>

          {error ? (
            <p
              role='alert'
              className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
            >
              {error}
            </p>
          ) : null}
        </div>

        <div className='flex items-center justify-end gap-2 border-t border-border px-6 py-4'>
          <Button
            type='submit'
            disabled={!canSend}
            className='bg-brand-navy text-white hover:bg-brand-navy/90'
          >
            {isLoading ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </form>
    </ControlledModal>
  );
};

export default ResendInviteModal;
